// @flow

import * as React from 'react';

import isEmpty from 'lodash/isEmpty';

import { Trash } from 'lucide-react';

import { useLocation, useMatch, useParams } from 'react-router-dom';
import { Button, Text, View } from 'reshaped';

import { selectOrder } from '@gandi/billing';
import { selectCartTotal, selectUnusedDiscounts } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useCurrency } from 'src/hooks/useCurrency';

import { CartSummaryPriceBreakdown } from '../CartSummaryPriceBreakdown';
import { DiscountBlock } from '../DiscountBlock';
import { MessagesBlock } from '../MessagesBlock';

import { Items } from './Items';
import { TotalArticles } from './TotalArticles';

type Props = {
  handleConfirmationRemove: React.ReactSetStateFunction<boolean>,
};

export function Total({
  handleConfirmationRemove,
}: Props): null | React.MixedElement {
  const { __ } = useTranslator();

  const params = useParams<{ orderId: string }>();
  const orderId = parseInt(params.orderId, 10);

  const order = useAppSelector(state => selectOrder(state, { orderId }));

  const currency = useCurrency();
  const total = useAppSelector(selectCartTotal);

  const unusedDiscounts = useAppSelector(selectUnusedDiscounts);

  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');
  const accountChoicePage = location.pathname.includes(
    '/configure/account-choice',
  );
  const generateProformaPage = location.pathname.includes('generate-proforma');
  const checkoutPage = location.pathname.includes('checkout');
  const accountStudioPage = Boolean(useMatch('/studio/:cartId/account'));

  if (isEmpty(total) || !currency) return null;
  const { beforeTaxes } = total;

  const hasDiscounts = !!(total.discounts?.length || unusedDiscounts?.length);

  const priceBeforeTaxes = orderId ? order?.price_before_taxes : beforeTaxes;
  const currentCurrency = orderId ? order?.currency : currency;

  return (
    <View gap={6} direction="column">
      <View justify="space-between" direction="row" align="center">
        <Text variant="featured-3" weight="bold" as="h3">
          {__('Cart Summary')}
        </Text>
        {overviewPage && (
          <Button
            icon={Trash}
            variant="ghost"
            attributes={{ title: __('Remove all products') }}
            // $FlowFixMe[incompatible-type]
            onClick={handleConfirmationRemove}
          />
        )}
      </View>
      <View gap={2}>
        <TotalArticles
          beforeTaxes={priceBeforeTaxes}
          currency={currentCurrency}
        />
        {(accountChoicePage || accountStudioPage) && (
          <Text as="p" variant="body-3">
            {__(
              'Prices, promotions and product options may depend on your rate scale, available offers, and other services associated with the products. Therefore, changes may occur after changing the billed organization.',
            )}{' '}
            {__('(Changes will be applied at the next step)')}
          </Text>
        )}
      </View>
      {accountChoicePage && <MessagesBlock />}
      {(checkoutPage || generateProformaPage) && <Items />}
      {overviewPage && <DiscountBlock hasDiscounts={hasDiscounts} />}
      {overviewPage && <CartSummaryPriceBreakdown />}
    </View>
  );
}
